import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { QrCode, Copy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog reutilizável para Pix via Mercado Pago.
 * Props:
 *  - open, onOpenChange(bool)
 *  - plan: { name, price }
 *  - createPayload: { student_id?, plan_id, payer_email?, pending_student? }
 *  - endpoints: { create: "/portal/mp/pix/create", status: "/portal/mp/pix/status/", sync: "/portal/mp/pix/sync/" }  (default = /payments/mp/*)
 *  - auth?: axios instance (opcional) — se não enviado, usa axios cru (público)
 *  - onPaid(chargeInfo): callback após baixa confirmada no backend
 */
export default function MpPixDialog({ open, onOpenChange, plan, createPayload, endpoints, auth, onPaid, title = "Pix via Mercado Pago" }) {
  const api = auth || axios.create({ baseURL: API });
  const ep = endpoints || { create: "/payments/mp/pix/create", status: "/payments/mp/status/", sync: "/payments/mp/sync/" };
  const cancelPath = ep.cancel || ep.create.replace(/\/create$/, "/cancel/");
  const [charge, setCharge] = useState(null);
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);
  const chargeRef = useRef(null);
  useEffect(() => { chargeRef.current = charge; }, [charge]);

  useEffect(() => {
    if (!open) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      const c = chargeRef.current;
      if (c && c.status !== "paid") {
        api.post(`${cancelPath}${c.external_reference}`).then(({ data }) => {
          if (data?.status === "paid") { onPaid && onPaid({ ...c, status: "paid" }); toast.success("Pix já tinha sido pago — pagamento confirmado!"); }
          else toast.info("Cobrança Pix cancelada");
        }).catch(() => {});
      }
      chargeRef.current = null;
      setCharge(null);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line
  }, [open]);

  const generate = () => {
    (async () => {
      setCreating(true);
      try {
        const { data } = await api.post(ep.create, createPayload);
        setCharge(data);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const { data: st } = await api.get(`${ep.status}${data.external_reference}`);
            if (st.status === "paid") {
              clearInterval(pollRef.current); pollRef.current = null;
              chargeRef.current = { ...data, status: "paid" };
              setCharge((c) => c ? { ...c, status: "paid" } : null);
              onPaid && onPaid({ ...data, ...st });
              toast.success("Pagamento Pix confirmado via Mercado Pago!");
              onOpenChange(false);
            }
          } catch (e) { /* ignora */ }
        }, 3000);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erro ao gerar Pix Mercado Pago");
        onOpenChange(false);
      } finally { setCreating(false); }
    })();
  };

  const forceSync = async () => {
    if (!charge) return;
    try {
      const { data } = await api.post(`${ep.sync}${charge.external_reference}`);
      if (data.status === "paid") {
        chargeRef.current = { ...charge, status: "paid" };
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        onPaid && onPaid({ ...charge, status: "paid" });
        onOpenChange(false);
      }
    } catch (e) { /* ignora */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mp-pix-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-rose-600"/>{title}</DialogTitle></DialogHeader>
        <div className="text-center space-y-3">
          {!creating && !charge && (
            <div className="space-y-3 py-2" data-testid="mp-pix-intro">
              {plan && <div className="font-heading text-3xl font-black text-rose-600">R$ {Number(plan.price).toFixed(2)}</div>}
              <p className="text-sm text-slate-600">O QR Code só é gerado quando você tocar no botão abaixo. Se fechar ou cancelar, a cobrança é cancelada no Mercado Pago na hora (ela também expira sozinha em 30 minutos).</p>
              <Button onClick={generate} className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase" data-testid="mp-pix-generate"><QrCode className="w-4 h-4 mr-2"/>Gerar QR Code Pix</Button>
              <Button variant="outline" onClick={()=>onOpenChange(false)} className="w-full" data-testid="mp-pix-back">Voltar</Button>
            </div>
          )}
          {creating && <div className="py-10 text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Gerando QR Code no Mercado Pago...</div>}
          {!creating && charge && (
            <>
              <p className="text-sm text-slate-600">Escaneie o QR Code abaixo com o app do seu banco. O status atualiza automaticamente em até 10 segundos.</p>
              {charge.qr_code_base64 ? (
                <img src={`data:image/png;base64,${charge.qr_code_base64}`} alt="QR Code Pix" className="w-64 h-64 mx-auto rounded-lg border" data-testid="mp-pix-qr-img"/>
              ) : (
                <div className="p-6 bg-slate-50 rounded-xl text-sm text-slate-500">Use o copia-e-cola abaixo</div>
              )}
              {plan && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  <div className="text-xs uppercase text-slate-500">Valor a pagar</div>
                  <div className="font-heading text-3xl font-black text-rose-600">R$ {Number(charge.amount || plan.price).toFixed(2)}</div>
                  <div className="text-xs text-slate-500 mt-1">{plan.name}</div>
                </div>
              )}
              {charge.qr_code && (
                <div className="space-y-1">
                  <Input readOnly value={charge.qr_code} className="font-mono text-xs text-center" data-testid="mp-pix-copy-input"/>
                  <Button variant="outline" size="sm" onClick={()=>{ navigator.clipboard.writeText(charge.qr_code); toast.success("Pix copia-e-cola copiado"); }} data-testid="mp-pix-copy-btn" className="w-full"><Copy className="w-4 h-4 mr-2"/>Copiar Pix copia-e-cola</Button>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
                <span>Aguardando pagamento... confirmação automática em até 10 segundos. Expira em 30 min.</span>
              </div>
              <div className="pt-1">
                <Button variant="outline" onClick={()=>onOpenChange(false)} className="w-full" data-testid="mp-pix-cancel">Cancelar cobrança</Button>
              </div>
              <p className="text-[11px] text-slate-400">Ref: {charge.external_reference}</p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
